import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Checkbox, Input, Form, Tooltip } from 'antd';
import getDefaultFields from './utils';
import AiAlertCustomisation from './AIAlertCustomisation';
import ManualAlertCustomisation from './ManualAlertCustomisation';
import VerifyModal from './VerifyModal';
import {
  AlertConfigRow,
  ContentWrapper,
  RequiredFieldsNote,
  FormWrapper,
  Separator,
  ButtonWrapper,
  CustomizeAlert,
  ConsentWrapper,
  ConsentPara,
  FormItemWrapper,
} from './styled';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { AlertType } from '../types';
import { useResetPanelAtoms } from '../hooks';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { useVerifyIntegration } from './hooks';

const EditCreateForm = ({ integrationData, form, onSave, isSaving }) => {
  const {
    name,
    emails,
    configId = null,
    alertType = null,
    state = 2,
    partnerId,
    partnerName,
    defaultFields = [],
  } = integrationData;
  const isAdminUser = useSelector(isAdminRoleSelector);
  const resetAtoms = useResetPanelAtoms();

  // for Service Verification Model
  const [openVerifyModel, setOpenVerifyModel] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const { mutate: verifyMutate, isLoading: isVerifying } = useVerifyIntegration(setIsVerified);

  const [consentCheck, setConsentCheck] = useState(false);
  const [consentEnabled, setConsentEnabled] = useState(true);

  // For AI/Manual customization model
  const [openAlertTypeModel, setOpenAlertTypeModel] = useState<AlertType | null>(null);
  const defaultFieldsForPartner = getDefaultFields(partnerName) || [];
  const displayManualAlerts = CURRENT_PRODUCT_MODE !== 'APP';

  useEffect(() => {
    if (configId) {
      setConsentCheck(true);
      setConsentEnabled(false);

      form.setFieldsValue({
        maConfig: integrationData?.alertConfig?.maConfig,
        aiConfig: integrationData?.alertConfig?.aiConfig,
      });
    }
  }, []);

  const onValuesChange = () => {
    setIsVerified(false);
  };

  const onConsentCheckboxChange = (e) => {
    setConsentCheck(e.target.checked);
  };

  const onVerify = async () => {
    try {
      const values = await form.validateFields();
      const config: any = {};
      defaultFieldsForPartner.forEach((cv) => (config[cv.name] = values[cv.name]));

      await verifyMutate({
        integration_type: partnerName,
        config,
      });
    } catch (errors) {
      return;
    }
  };

  const handleSaveButton = async () => {
    try {
      const values = await form.validateFields();
      const vendorFields: any = [];
      const { name, emails } = values;

      let alertType = '';
      const { manualAlert, aiAlert } = values;
      if (manualAlert && aiAlert) {
        alertType = 'ALL';
      } else if (manualAlert) {
        alertType = 'MA';
      } else if (aiAlert) {
        alertType = 'AI';
      }

      defaultFieldsForPartner.forEach((field) => {
        if (field.name in values) {
          vendorFields.push({ name: field.name, value: values[field.name] });
        }
      });

      const aiConfig = form.getFieldValue('aiConfig');
      const maConfig = form.getFieldValue('maConfig');

      const alertConfig = {
        aiConfig: aiConfig ?? {
          severity: null,
          customFields: [],
        },
        maConfig: maConfig ?? {
          priority: null,
          customFields: [],
        },
      };

      const configuration = {
        configId,
        name,
        emails,
        alertType,
        state: state === 3 ? 2 : state,
        partnerId,
        partnerName,
        defaultFields: vendorFields,
        alertConfig,
      };

      if (configId) {
        await onSave({ configuration });
      } else {
        delete configuration.configId;
        await onSave({ configuration });
      }
    } catch (e) {
      return;
    }
  };

  const handleCancleButton = () => {
    resetAtoms.reset();
  };

  const handleVerifyButton = () => {
    setOpenVerifyModel(true);
    onVerify();
  };

  const handleAlertModelSave = (field, valuesFromModal) => {
    form.setFieldValue(field, valuesFromModal);
    setOpenAlertTypeModel(null);
  };

  const RequiredKey = <span style={{ color: 'red' }}>*</span>;

  return (
    <ContentWrapper>
      <RequiredFieldsNote>Fields are mandatory</RequiredFieldsNote>
      <FormWrapper>
        <Form
          layout="horizontal"
          form={form}
          onValuesChange={onValuesChange}
          disabled={!isAdminUser || state === 1}
        >
          {/* <FormItemWrapper>
            <Form.Item
              label="Service Type"
              name="username"
              initialValue={partnerName}
              colon={false}
            >
              <Input disabled value={partnerName} />
            </Form.Item>
          </FormItemWrapper> */}

          <FormItemWrapper>
            <Form.Item
              label={
                <Tooltip
                  title="Enter the name of this channel. This name will appear in emails about the channel."
                  placement="topLeft"
                >
                  Name{RequiredKey}
                </Tooltip>
              }
              colon={false}
              name="name"
              rules={[{ required: true, message: 'Please enter a name' }]}
              initialValue={name}
            >
              <Input />
            </Form.Item>
          </FormItemWrapper>

          {defaultFieldsForPartner.map((configItem) => (
            <FormItemWrapper key={configItem.name}>
              <Form.Item
                key={configItem.name}
                label={
                  <Tooltip title={configItem.helpText} placement="topLeft">
                    {configItem.label}
                    {RequiredKey}
                  </Tooltip>
                }
                name={configItem.name}
                colon={false}
                rules={[
                  {
                    required: configItem.required,
                    message: configItem.errorText,
                  },
                ]}
                initialValue={
                  (defaultFields.find((item) => item.name === configItem.name) || { value: '' })
                    .value
                }
              >
                <Input type={configItem.type} />
              </Form.Item>
            </FormItemWrapper>
          ))}

          <FormItemWrapper>
            <Form.Item
              label={
                <Tooltip
                  title="Specify the email addresses that will receive alert emails."
                  placement="topLeft"
                >
                  Email{RequiredKey}
                </Tooltip>
              }
              name="emails"
              colon={false}
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    const emailArray = value.split(',').map((email) => email.trim());
                    const re =
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return emailArray.every((email) => re.test(email)) && emailArray.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('Please enter valid emails separated by commas.'));
                  },
                },
              ]}
              initialValue={emails}
            >
              <Input />
            </Form.Item>
          </FormItemWrapper>

          <AlertConfigRow>
            <Form.Item
              label={
                <Tooltip
                  title="Set the severity and custom payloads for AI Alerts."
                  placement="topLeft"
                >
                  AI Alerts
                </Tooltip>
              }
              initialValue={alertType === 'AI' || alertType === 'ALL'}
              name="aiAlert"
              colon={false}
              valuePropName="checked"
              rules={[{ required: false }]}
            >
              <Checkbox />
            </Form.Item>
            <CustomizeAlert
              style={{ marginBottom: 24 }}
              onClick={() => setOpenAlertTypeModel('AI')}
            >
              Customize AI Alerts
            </CustomizeAlert>
            <AiAlertCustomisation
              openModel={openAlertTypeModel}
              onSaveModel={handleAlertModelSave}
              onCancelModel={() => setOpenAlertTypeModel(null)}
              form={form}
            />
          </AlertConfigRow>

          {displayManualAlerts && (
            <AlertConfigRow>
              <Form.Item
                label={
                  <Tooltip
                    title="Set the priority and custom payloads for Manual Alerts."
                    placement="topLeft"
                  >
                    Manual Alerts
                  </Tooltip>
                }
                name="manualAlert"
                colon={false}
                valuePropName="checked"
                initialValue={alertType === 'MA' || alertType === 'ALL'}
                rules={[{ required: false }]}
              >
                <Checkbox />
              </Form.Item>
              <CustomizeAlert
                style={{ marginBottom: 24 }}
                onClick={() => setOpenAlertTypeModel('MA')}
              >
                Customize Manual Alerts
              </CustomizeAlert>
              <ManualAlertCustomisation
                openModel={openAlertTypeModel}
                onSaveModel={handleAlertModelSave}
                onCancelModel={() => setOpenAlertTypeModel(null)}
                form={form}
              />
            </AlertConfigRow>
          )}
        </Form>

        <ConsentWrapper>
          <Checkbox
            onChange={onConsentCheckboxChange}
            checked={consentCheck}
            disabled={!consentEnabled}
          />
          <ConsentPara>
            By clicking here, you authorize Conviva to share Conviva alert data with the person or
            entity to whom this
            {` ${partnerName} `}
            integration is directed, and you acknowledge and agree that Conviva shall have no
            responsibility or liability for such person’s or entity’s use of this data.
          </ConsentPara>
        </ConsentWrapper>
        <Separator />

        <ButtonWrapper>
          <Button onClick={handleCancleButton}>
            {isAdminUser || state == 1 ? 'Cancel' : 'Back'}
          </Button>
          {!isVerified ? (
            <Button
              type="primary"
              onClick={handleVerifyButton}
              disabled={!consentCheck || !isAdminUser || state == 1}
              loading={isVerifying}
            >
              Verify
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleSaveButton}
              disabled={!consentCheck || !isAdminUser || state == 1}
              loading={isSaving}
            >
              {configId ? 'Update' : 'Save'}
            </Button>
          )}
        </ButtonWrapper>
      </FormWrapper>
      <VerifyModal
        partnerName={partnerName}
        openVerifyModel={openVerifyModel}
        setOpenVerifyModel={setOpenVerifyModel}
      />
    </ContentWrapper>
  );
};

export default EditCreateForm;
